import { ReactNode, useCallback, useEffect, useState } from 'react';
import AuthContext from '@/modules/auth/contexts/AuthContext';
import { User, UserContext } from '@/modules/auth/models/User';
import { UserRole } from '@/modules/auth/models/UserRole';
import AuthService, { IdToken } from '@/modules/auth/services/AuthService';

interface AuthState {
  isLoggedIn: boolean;
  accessToken?: string;
  idToken?: string;
  role?: UserRole;
  roles: UserRole[];
  isInitialized?: boolean;
}

interface AuthorizationProviderProps {
  children: ((state: AuthState) => ReactNode) | ReactNode;
}

const authService = new AuthService();

export const AuthProvider = ({ children }: AuthorizationProviderProps) => {
  const [state, setState] = useState<AuthState>({
    isLoggedIn: false,
    accessToken: undefined,
    idToken: undefined,
    role: undefined,
    roles: [],
    isInitialized: false,
  });

  const login = async (username: string, password: string) => {
    const token = await authService.login(username, password);
    if (!token) {
      return false;
    }

    const { role, roles } = token;

    setState((prevState) => {
      return {
        ...prevState,
        isLoggedIn: true,
        accessToken: token.accessToken,
        idToken: token.idToken,
        role: role,
        roles: roles,
      };
    });

    return true;
  };

  const logout = useCallback(async () => {
    await authService.logout();
    setState((prevState) => {
      return {
        ...prevState,
        isLoggedIn: false,
        accessToken: undefined,
        idToken: undefined,
        role: undefined,
        roles: [],
      };
    });
  }, []);

  const switchContext = async (userContext: UserContext) => {
    if (!state.accessToken) return false;

    try {
      const token = await authService.withAccessToken(state.accessToken).switchContext(userContext);
      if (!token) return false;

      const { role, roles } = token;

      setState((prev) => ({
        ...prev,
        isLoggedIn: true,
        accessToken: token.accessToken,
        idToken: token.idToken,
        role: role,
        roles: roles,
      }));
      return true;
    } catch (err) {
      console.error('Error switching context:', err);
      return false;
    }
  };

  const resetContext = () => switchContext({});

  const changeRole = async (newRole: UserRole) => {
    if (!state.roles.includes(newRole)) {
      return;
    }

    await resetContext();

    await authService.changeRole(newRole);
    setState((prevState) => ({
      ...prevState,
      role: newRole,
    }));
  };

  const convertIdTokenToUser = (idToken: IdToken): User | undefined => {
    const role = determineUserRole(idToken.roles, idToken);
    if (!role) {
      return;
    }

    return {
      id: idToken.sub || '',
      username: idToken.username ?? '',
      email: idToken.email ?? '',
      firstName: idToken.firstName ?? '',
      lastName: idToken.lastName ?? '',
      fullName: idToken.fullName ?? '',
      role: role,
      status: 'ACTIVE',
      currencyCode: '',
      languageCode: '',
      timezone: '',
      organizationId: idToken.organizationId,
      retailerId: idToken.retailerId,
      storeId: idToken.storeId,
      supplierId: idToken.supplierId,
    };
  };

  const determineUserRole = (roles?: string[], userContext?: UserContext): UserRole | undefined => {
    if (userContext?.storeId) return UserRole.StoreManager;
    if (userContext?.supplierId) return UserRole.SupplierAdmin;

    if (!roles || roles.length === 0) return undefined;

    if (roles.includes(UserRole.StoreManager)) return UserRole.StoreManager;
    if (roles.includes(UserRole.RetailerAdmin)) return UserRole.RetailerAdmin;
    if (roles.includes(UserRole.SupplierAdmin)) return UserRole.SupplierAdmin;
    if (roles.includes(UserRole.SystemAdmin)) return UserRole.SystemAdmin;

    return undefined;
  };

  const isExpired = (exp?: number | null): boolean => {
    if (!exp) return true;
    const nowInSeconds = Math.floor(Date.now() / 1000);
    return exp < nowInSeconds;
  };

  const currentUser = (): Partial<User> => {
    if (!state.isLoggedIn || !state.idToken) {
      return {};
    }

    const idToken = authService.decodeIdToken(state.idToken);
    if (isExpired(idToken.exp)) {
      return {};
    }

    return convertIdTokenToUser(idToken) || {};
  };

  const { isInitialized } = state;

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    let isMounted = true;

    const getToken = async () => {
      const token = await authService.getToken();

      const { role, roles = [], idToken } = token || {};

      const { exp } = (idToken && authService.decodeIdToken(idToken)) || {};

      if (isMounted) {
        setState((prevState) => ({
          isLoggedIn: !!token && !isExpired(exp),
          accessToken: token?.accessToken,
          idToken: token?.idToken,
          role: prevState.role || role,
          roles: roles,
          isInitialized: true,
        }));
      }
    };

    void getToken();

    return () => {
      isMounted = false;
    };
  }, [isInitialized]);

  const { isLoggedIn, accessToken, idToken, role, roles } = state;

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        accessToken,
        idToken,
        role,
        roles,
        changeRole,
        currentUser,
        login,
        logout,
        switchContext,
      }}
    >
      {isInitialized && typeof children === 'function'
        ? children(state)
        : isInitialized && typeof children !== 'function'
          ? children
          : undefined}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
